import React, { useState } from 'react';
import { Formik } from 'formik';
import { Box, Button } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import { isEmpty } from 'lodash/fp';
import CloseIcon from '@material-ui/icons/Close';
import {
    InputField, MultipleAutocompleteField, SelectField, SwitchField,
} from '../../../../components/forms/fields';
import FormWrapper from '../../../../components/forms/FormWrapper';
import { Options } from '../../../../components/forms/fields/types';
import { mapDevicesToFormProps } from '../../utils';
import { generateTranslationByVenueType, getChildCategoryOptions, sanitizeFormValues } from '../../../../utils';
import { useUser } from '../../../../utils/hooks/auth';
import { Category, Device, Venue } from '../../../../server-types';
import { READ_POHODA_CENTRE_ID } from '../../../../graphql/queries';
import { Typography } from '../../../../ui-kit';
import ProtectedComponent from '../../../../utils/routes/ProtectedComponent';
import { VenueType } from '../../../../generated/graphql';
import { productValidationSchema } from './validation';
import { ProductFormProps, ProductFormValues, RecipeLineFormValue } from './types';
import { CategorySelect } from './CategorySelect';
import WarehouseCardField from './WarehouseCardField';
import RecipeBuilder from './RecipeBuilder';
import { EMPTY_RECIPE_LINE } from './constants';

const getOptionSelected = (option: Options, value: Options) => option.id === value.id;
const RECIPE_LINES_FIELD_NAME = 'recipeLines';

const ProductForm = ({
    warehouses,
    submitText,
    categories,
    taxes,
    devices,
    onSubmit,
    initialValues,
    connectedWarehouseCard,
}: ProductFormProps) => {
    const [isWarehouseCardSearchShown, setIsWarehouseCardSearchShown] = useState(!!connectedWarehouseCard);
    const [isRecipeBuilderShown, setIsRecipeBuilderShown] = useState(!isEmpty(initialValues?.recipeLines));
    const { user: { venueType } } = useUser();

    const { t } = useTranslation();
    const defaultValues: ProductFormValues = {
        name: '',
        description: '',
        categoryId: '',
        taxId: '',
        toGoTaxId: '',
        printingDeviceIds: [],
        priceSell: '',
        pohodaStockItemId: '',
        showInPos: true,
        displayName: '',
        imageUrl: '',
        warehouseCardId: '',
        warehouseId: '',
        recipeLines: [],
    };

    const { user } = useUser();
    const client = useApolloClient();
    const venue = client.readFragment<Venue>({
        id: `Venue:${user.currentVenueId}`,
        fragment: READ_POHODA_CENTRE_ID,
    });

    const showPohodaExport = venue && !!venue.pohodaCentreId;

    const categoryOptions = getChildCategoryOptions(categories as Category[]);

    const printingDevicesOptions = mapDevicesToFormProps(devices as Device[]);

    const handleSubmit = async ({ recipeLines = [], ...values }: ProductFormValues) => {
        const sanitizedRecepieLines = recipeLines.reduce((acc: Array<RecipeLineFormValue>, recipeLine) => {

            if (recipeLine.stockCardId !== '') {
                acc.push(recipeLine);
            }

            return acc;
        }, []);

        const sanitizedValues = {
            ...values,
            recipeLines: sanitizedRecepieLines,
        };

        const finalValues = sanitizeFormValues(sanitizedValues);
        return onSubmit(finalValues);
    };

    const warehouseIds = warehouses?.map(({ id }) => id);

    return (
        <Formik
            initialValues={{ ...defaultValues, ...initialValues }}
            validationSchema={productValidationSchema}
            onSubmit={handleSubmit}
        >
            {formikProps => (
                <FormWrapper submitText={submitText} isSubmitting={formikProps.isSubmitting}>
                    <Box my={2} display="flex" flexDirection="column">
                        <InputField<ProductFormValues> name="name" label={t('Name')} formikProps={formikProps} required />
                        <CategorySelect
                            variant="outlined"
                            name="categoryId"
                            label={t('Category')}
                            options={categoryOptions}
                            formikProps={formikProps}
                            required
                        />
                        <InputField<ProductFormValues> name="description" label={t('Description')} formikProps={formikProps} />
                        <ProtectedComponent allowedVenueTypes={[VenueType.Restaurant]}>
                            <MultipleAutocompleteField
                                name='printingDeviceIds'
                                label={t('Printers')}
                                options={printingDevicesOptions}
                                getOptionSelected={getOptionSelected}
                            />
                        </ProtectedComponent>
                    </Box>
                    <Box my={2} display="flex" flexDirection="column">
                        <Typography variant="h5">{t('Prices')}</Typography>
                        {showPohodaExport
                            ? <InputField name="pohodaStockItemId" label={t('Pohoda ID')} type="text" formikProps={formikProps} />
                            : null}
                        <InputField name="priceSell" label={t('Price sell')} type="number" formikProps={formikProps} required />
                        <SelectField variant="outlined" options={taxes} name="taxId" label={t('Tax')} formikProps={formikProps} required />
                        <ProtectedComponent allowedVenueTypes={[VenueType.Restaurant]}>
                            <SelectField
                                variant="outlined"
                                options={taxes}
                                name="toGoTaxId"
                                label={t('ToGo Tax')}
                                formikProps={formikProps}
                            />
                        </ProtectedComponent>
                        <SwitchField name="showInPos" label={t('Show in pos')} formikProps={formikProps} />
                        <Box my={2} display="flex" flexDirection="column">
                            <Typography variant="h5">{t('Inventory')}</Typography>
                            <Box mb={3} />
                            {warehouseIds && warehouseIds.length > 0 ? (
                                <>
                                    <Box display="flex" justifyContent="stretch">
                                        {!isRecipeBuilderShown && (
                                        <Button
                                            fullWidth
                                            onClick={() => {
                                            if (isWarehouseCardSearchShown) {
                                                const { warehouseCardId } = formikProps.values;
                                                if (warehouseCardId) {
                                                    formikProps.setFieldValue('warehouseCardId', null);
                                                    formikProps.setFieldValue('warehouseId', null);
                                                }
                                            }
                                            return setIsWarehouseCardSearchShown(!isWarehouseCardSearchShown);
                                        }}
                                            variant="outlined"
                                            color={isWarehouseCardSearchShown ? 'secondary' : 'primary'}
                                        >
                                            {isWarehouseCardSearchShown
                                            ? t('Disconnect from warehouse card')
                                            : t('Connect to a warehouse card')}
                                        </Button>
                                        )}
                                        {!isWarehouseCardSearchShown && !isRecipeBuilderShown && <Box mr={2} />}
                                        {!isWarehouseCardSearchShown && (
                                        <Button
                                            fullWidth
                                            startIcon={isRecipeBuilderShown ? <CloseIcon /> : null}
                                            onClick={() => {
                                                formikProps.setFieldValue('recipeLines', isRecipeBuilderShown ? [] : [EMPTY_RECIPE_LINE]);
                                                setIsRecipeBuilderShown(!isRecipeBuilderShown);
                                        }}
                                            variant="outlined"
                                            color={isRecipeBuilderShown ? 'secondary' : 'primary'}
                                        >
                                            {!isRecipeBuilderShown
                                            ? generateTranslationByVenueType({
                                                translations: {
                                                    Ecommerce:  t('Build a composition'),
                                                    Restaurant: t('Build a recipe'),
                                                },
                                                venueType,
                                            })
                                            : generateTranslationByVenueType({
                                                translations: {
                                                    Ecommerce:  t('Cancel composition'),
                                                    Restaurant: t('Cancel recipe'),
                                                },
                                                venueType,
                                            })}
                                        </Button>
                                        )}
                                    </Box>
                                    {!isRecipeBuilderShown && isWarehouseCardSearchShown && (
                                    <WarehouseCardField
                                        avaialbleWarehouseIds={warehouseIds}
                                        searchFieldProps={{
                                            helperText: t('We show you only cards which are stored in pieces'),
                                            label: t('Connected warehouse card'),
                                            currentWarehouseCard: connectedWarehouseCard,
                                            warehouseIds,
                                            name: 'warehouseCardId'
                                        }}
                                        selectFieldProps={{
                                            variant: 'outlined',
                                            name: 'warehouseId',
                                            label: t('Warehouse'),
                                            formikProps
                                        }}
                                    />
                                    )}
                                    {!isWarehouseCardSearchShown && isRecipeBuilderShown && (
                                        <RecipeBuilder
                                            removeRecipe={() => {
                                                formikProps.setFieldValue(RECIPE_LINES_FIELD_NAME, []);
                                                setIsRecipeBuilderShown(!isRecipeBuilderShown);
                                            }}
                                            name={RECIPE_LINES_FIELD_NAME}
                                            warehouseIds={warehouseIds}
                                        />
                                    )}
                                </>
                            ) : <Typography>{t('You can\'t create recepies without created warehouse')}</Typography>}
                        </Box>
                    </Box>
                </FormWrapper>
            )}
        </Formik>
    );
};

export default ProductForm;
